import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import PropertyCard from '@/components/site/PropertyCard';
import api from '@/lib/api';

const defaults = { location: '', property_type: 'all', min_area: '', max_area: '', min_budget: '', max_budget: '', q: '' };

function FiltersForm({ filters, setFilters, onApply, onClear, inSheet = false }) {
  return (
    <div className={`space-y-4 ${inSheet ? '' : 'sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">Refine</p>
      <div>
        <label className="text-xs font-semibold text-slate-600">Search</label>
        <Input className="mt-1" placeholder="Title, location…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filters-q" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Location</label>
        <Input className="mt-1" placeholder="e.g. Gachibowli" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="filters-location" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Property Type</label>
        <Select value={filters.property_type} onValueChange={(v) => setFilters({ ...filters, property_type: v })}>
          <SelectTrigger data-testid="filters-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="office">Office</SelectItem>
            <SelectItem value="retail">Retail</SelectItem>
            <SelectItem value="coworking">Coworking</SelectItem>
            <SelectItem value="mixed-use">Mixed-Use</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600">Min Area (sqft)</label>
          <Input type="number" className="mt-1" value={filters.min_area} onChange={(e) => setFilters({ ...filters, min_area: e.target.value })} data-testid="filters-min-area" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Max Area (sqft)</label>
          <Input type="number" className="mt-1" value={filters.max_area} onChange={(e) => setFilters({ ...filters, max_area: e.target.value })} data-testid="filters-max-area" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Min Budget</label>
          <Input type="number" className="mt-1" placeholder="₹/sqft" value={filters.min_budget} onChange={(e) => setFilters({ ...filters, min_budget: e.target.value })} data-testid="filters-min-budget" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Max Budget</label>
          <Input type="number" className="mt-1" placeholder="₹/sqft" value={filters.max_budget} onChange={(e) => setFilters({ ...filters, max_budget: e.target.value })} data-testid="filters-max-budget" />
        </div>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <Button onClick={onApply} className="flex-1 bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="properties-filters-apply-button"><Search className="mr-2 h-4 w-4" /> Apply</Button>
        <Button variant="outline" onClick={onClear} className="border-[#C6A35B] text-[#144726] hover:bg-[#C6A35B]/10" data-testid="properties-filters-clear-button">Clear</Button>
      </div>
    </div>
  );
}

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ ...defaults });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Sync filters from URL params (initial)
  useEffect(() => {
    const next = { ...defaults };
    ['location', 'property_type', 'min_area', 'max_area', 'min_budget', 'max_budget', 'q'].forEach((k) => {
      const v = params.get(k);
      if (v) next[k] = v;
    });
    setFilters(next);
  }, []); // eslint-disable-line

  const fetchList = (f = filters) => {
    setLoading(true);
    const q = {};
    Object.entries(f).forEach(([k, v]) => { if (v && (k !== 'property_type' || v !== 'all')) q[k] = v; });
    api.get('/properties', { params: q })
      .then((res) => setItems(res.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(filters); /* eslint-disable-next-line */ }, [params]);

  const applyFilters = () => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && (k !== 'property_type' || v !== 'all')) next.set(k, v); });
    setParams(next);
    setSheetOpen(false);
  };
  const clearFilters = () => {
    setFilters({ ...defaults });
    setParams(new URLSearchParams());
  };

  useEffect(() => { document.title = 'Commercial Properties for Lease in Hyderabad | Cespedis'; }, []);

  const empty = !loading && items.length === 0;

  return (
    <div data-testid="properties-page">
      <section className="relative isolate overflow-hidden bg-[#144726] pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#144726] via-[#1A5A33] to-[#144726]" />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C6A35B]">Properties</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight">Available commercial properties.</h1>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-white/75">Filter by location, type, area, and budget to shortlist commercial assets across Hyderabad.</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            {/* Desktop filters */}
            <aside className="hidden lg:block lg:col-span-3">
              <FiltersForm filters={filters} setFilters={setFilters} onApply={applyFilters} onClear={clearFilters} />
            </aside>

            <div className="lg:col-span-9">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-slate-600">{loading ? 'Loading…' : `${items.length} ${items.length === 1 ? 'property' : 'properties'}`} found</div>
                <div className="lg:hidden">
                  <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="border-[#144726] text-[#144726]" data-testid="properties-filters-open-button"><SlidersHorizontal className="mr-2 h-4 w-4" /> Filters</Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[88%] max-w-md bg-white">
                      <div className="mt-6">
                        <FiltersForm filters={filters} setFilters={setFilters} onApply={applyFilters} onClear={clearFilters} inSheet />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
              </div>

              {empty ? (
                <div className="mt-10 rounded-xl border border-dashed border-slate-300 p-10 text-center">
                  <p className="font-display text-2xl font-semibold text-[#144726]">No matching properties</p>
                  <p className="mt-2 text-sm text-slate-600">Try widening your filters or clearing them to see all listings.</p>
                  <Button onClick={clearFilters} className="mt-5 bg-[#144726] text-white hover:bg-[#0E3520]"><X className="mr-2 h-4 w-4" /> Clear filters</Button>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {loading && Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="animate-pulse rounded-xl border border-slate-200 bg-white">
                      <div className="aspect-[16/10] w-full bg-slate-100" />
                      <div className="space-y-2 p-5"><div className="h-5 w-3/4 rounded bg-slate-100" /><div className="h-4 w-1/2 rounded bg-slate-100" /></div>
                    </div>
                  ))}
                  {!loading && items.map((p) => <PropertyCard key={p.id} property={p} testId="properties-list-card" />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
